import { createContext, ReactNode, useContext, useRef, useState } from "react";

type ContextType = {
  isExpandedRef: React.MutableRefObject<boolean>;
  leftColRef: React.RefObject<HTMLDivElement>;
  rightColRef: React.RefObject<HTMLDivElement>;
  toggleExpand: () => void;
  isCardExpanded: boolean;
};

const DashCardExpandContext = createContext<ContextType | null>(null);

export function DashCardExpandContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const isExpandedRef = useRef(false);
  const leftColRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const [isCardExpanded, setIsCardExpanded] = useState(false);

  const toggleExpand = () => {
    const isExpanded = (isExpandedRef.current = !isExpandedRef.current);
    setIsCardExpanded(isExpanded);

    if (leftColRef.current) {
      // Instead of hiding instantly, animate max-width to 0
      leftColRef.current.classList.toggle("max-w-[300px]", !isExpanded);
      leftColRef.current.classList.toggle("max-w-0", isExpanded);
      leftColRef.current.classList.toggle("opacity-100", !isExpanded);
      leftColRef.current.classList.toggle("opacity-0", isExpanded);

      leftColRef.current.parentElement?.classList.toggle("gap-6", !isExpanded);
      leftColRef.current.classList.toggle("gap-6", !isExpanded);
    }

    if (rightColRef.current) {
      rightColRef.current.classList.toggle("flex-[2]", isExpanded);
      rightColRef.current.classList.toggle("flex-1", !isExpanded);
    }
  };

  return (
    <DashCardExpandContext.Provider
      value={{
        isExpandedRef,
        leftColRef,
        rightColRef,
        toggleExpand,
        isCardExpanded,
      }}
    >
      {children}
    </DashCardExpandContext.Provider>
  );
}

export function useCardExpandContext() {
  const context = useContext(DashCardExpandContext);
  if (context === null) {
    throw new Error(" must be used within an AppProvider");
  }
  return context;
}
